import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_META } from "constants/misc";
import { Meta, Query, SortDirection } from "interfaces/api.interface";
import { IAddress } from "models/address/address.model";
import {
  bulkDeleteAddresses,
  deleteAddress,
  fetchAddresses,
  setDefaultAddress,
  removeDefaultAddress,
  getDefaultAddress,
  validateAddress,
  validateAllAddresses,
} from "services/api/addresses";
import { FetchAddressesResponseDto } from "services/api/addresses/dtos/fetch-addresses.response.dto";
import { AppThunk, RootState } from "store";
import { isModalClosed } from "store/modalSlice/modalSlice.actions";
import { modals } from "store/modalSlice/modalSlice.types";
import toast from "react-hot-toast";
import { GENERIC_TOAST_DELETED_MESSAGE } from "constants/strings";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { setDefaultFromAddress } from "store/createShipmentSlice/index.slice";
import {
  describeAddressValidation,
  parseValidatedBy,
} from "utils/address/addressValidation.util";

export interface IAddressesSlice {
  loadingStatus: ApiLoadingStatus;
  editingAddress: IAddress | null;
  query: Query<IAddress, { search?: string }>;
  items: IAddress[];
  meta: Meta;
  selectedAddressUuids: IAddress["uuid"][];
}

export const AddressSliceInitialState: IAddressesSlice = {
  loadingStatus: ApiLoadingStatus.IDLE,
  editingAddress: null,
  query: {
    filters: { search: "" },
    sort: {
      createdDate: SortDirection.DESC,
    },
  },
  meta: {
    ...DEFAULT_META,
  },
  items: [],
  selectedAddressUuids: [],
};

export const addressSlice = createSlice({
  name: "addressSlice",
  initialState: AddressSliceInitialState,
  reducers: {
    setData: (state, action: PayloadAction<FetchAddressesResponseDto>) => {
      state.items = action.payload.data || AddressSliceInitialState.items;

      state.meta = action.payload.meta || AddressSliceInitialState.meta;

      const visibleUuids = new Set(state.items.map((item) => item.uuid));
      state.selectedAddressUuids = state.selectedAddressUuids.filter((uuid) =>
        visibleUuids.has(uuid)
      );

      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },
    setQuerySort: (
      state,
      action: PayloadAction<IAddressesSlice["query"]["sort"]>
    ) => {
      state.query.sort = action.payload;
    },
    setQuerySearch: (
      state,
      action: PayloadAction<IAddressesSlice["query"]["filters"]["search"]>
    ) => {
      state.query.filters.search = action.payload;
      state.query.offset = 0;
    },
    setStatus: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.loadingStatus = action.payload;
    },
    setEditingAddress: (state, action: PayloadAction<IAddress | null>) => {
      state.editingAddress = action.payload;
    },
    setSelectedAddressUuids: (
      state,
      action: PayloadAction<IAddressesSlice["selectedAddressUuids"]>
    ) => {
      state.selectedAddressUuids = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isModalClosed,
      (state, action: { payload: { modalName: modals } }) => {
        if (
          action.payload.modalName === "createReceiverAddress" ||
          action.payload.modalName === "createCompanyAddress"
        ) {
          state.editingAddress = null;
        }
      }
    );
  },
});

export const {
  setData,
  setStatus,
  setEditingAddress,
  setQuerySort,
  setQuerySearch,
  setSelectedAddressUuids,
} = addressSlice.actions;

export const fetchDefaultAddressThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const { data } = await getDefaultAddress(salesAccountUUID);
      if (data) {
        dispatch(setDefaultFromAddress(data));
      }
    }
  };
};

export const fetchAddressesThunk = (query: Query): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      addressSlice,
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    const {
      filters: { search },
    } = addressSlice.query;
    if (salesAccountUUID) {
      const { data, error, meta } = await fetchAddresses(salesAccountUUID, {
        ...query,
        filters: { ...query.filters, ...(search && { search }) },
        limit: numberOfRowsSelected.value,
      });

      if (!error) {
        dispatch(setData({ data, meta }));
      }
    }
  };
};

export const deleteAddressThunk = (addressUuid: IAddress["uuid"]): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      await deleteAddress(salesAccountUUID, addressUuid);

      dispatch(fetchAddressesThunk({ offset: 0 }));

      toast.success(GENERIC_TOAST_DELETED_MESSAGE);
    }
  };
};

export const bulkDeleteAddressesThunk = (): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
      addressSlice: { selectedAddressUuids },
    } = getState() as RootState;

    if (!salesAccountUUID || selectedAddressUuids.length === 0) {
      dispatch(setStatus(ApiLoadingStatus.IDLE));
      return false;
    }

    const response = await bulkDeleteAddresses(
      salesAccountUUID,
      selectedAddressUuids
    );

    if (response?.error) {
      dispatch(setStatus(ApiLoadingStatus.FAILED));
      toast.error("Failed to delete selected addresses");
      return false;
    }

    const deletedCount = response?.data?.deleted ?? selectedAddressUuids.length;

    dispatch(setSelectedAddressUuids([]));
    dispatch(fetchAddressesThunk({ offset: 0 }));
    toast.success(
      `${deletedCount} address${deletedCount === 1 ? "" : "es"} deleted`
    );

    return true;
  };
};

export const setAsFromAddressDefaultForSingleShipmentThunk = (
  addressUuid: IAddress["uuid"]
) => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const response = await setDefaultAddress(salesAccountUUID, addressUuid);
      dispatch(fetchAddressesThunk({ offset: 0 }));

      if (response?.statusCode === 200) {
        toast.success("Default address set successfully");
      } else {
        toast.error("Failed to set default address");
      }
    }
  };
};

export const removeAddressDefaultForSingleShipmentThunk = (
  addressUuid: IAddress["uuid"]
) => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const response = await removeDefaultAddress(
        salesAccountUUID,
        addressUuid
      );

      dispatch(fetchAddressesThunk({ offset: 0 }));

      if (response?.statusCode === 200) {
        toast.success("Removed default address successfully");
      } else {
        toast.error("Failed to remove default address");
      }
    }
  };
};

export const searchAddressThunk = (query: string): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQuerySearch(query));

    dispatch(fetchAddressesThunk({ offset: 0 }));
  };
};

/** Re-checks one address with UPS and DHL (the row action) and reports the verdict. */
export const validateAddressThunk = (addressUuid: IAddress["uuid"]): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (!salesAccountUUID) return;

    const toastId = toast.loading("Validating address…");
    const response = await validateAddress(salesAccountUUID, addressUuid);

    if (response?.error || !response?.data) {
      toast.error(response?.message || "Failed to validate address", {
        id: toastId,
      });
      return;
    }

    const { ups, dhl } = parseValidatedBy(response.data.validatedBy);
    const summary = describeAddressValidation(response.data);
    if (ups || dhl) {
      toast.success(summary, { id: toastId });
    } else {
      toast.error(summary, { id: toastId });
    }

    dispatch(fetchAddressesThunk({ offset: 0 }));
  };
};

// The server validates in the background and the list only learns the result
// on a refetch, so poll a few times after starting a run.
const VALIDATE_ALL_REFETCH_DELAYS_MS = [3000, 8000, 15000];

/** Validates the whole address book in the background (the header button). */
export const validateAllAddressesThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (!salesAccountUUID) return;

    const response = await validateAllAddresses(salesAccountUUID);

    if (response?.error || !response?.data) {
      toast.error(response?.message || "Failed to start address validation");
      return;
    }

    const { queued, alreadyRunning } = response.data;

    if (alreadyRunning) {
      toast("Address validation is already running for this address book");
      return;
    }
    if (queued === 0) {
      toast("No addresses to validate");
      return;
    }

    toast.success(
      `Validating ${queued} address${queued === 1 ? "" : "es"} in the background`
    );
    VALIDATE_ALL_REFETCH_DELAYS_MS.forEach((delay) => {
      setTimeout(() => dispatch(fetchAddressesThunk({ offset: 0 })), delay);
    });
  };
};
